import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dropdown,
  DropdownMenu,
  Button,
  Icon,
  MenuSection,
  ConfirmationModal,
  Tooltip,
} from '@folio/stripes-components';
import { FormattedMessage } from 'react-intl';
import { IfPermission, useStripes } from '@folio/stripes-core';
import FavoriteButton from '../../../../favorites/FavoriteButton';
import ScheduleReportModal from '../../../../scheduled/ScheduleReportModal';

function ReportDetailsActions({ report, onDelete, canEdit = true, location }) {
  const reportId   = report?.id;
  const reportName = report?.name;
  const [dropdownOpen, setdropdownOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const stripes = useStripes();

  const hasAnyAction =
    stripes.hasPerm('ui-reports.favorite-reports.manage') ||
    stripes.hasPerm('ui-reports.scheduled-reports.create') ||
    stripes.hasPerm('ui-reports.reports.edit') ||
    stripes.hasPerm('ui-reports.reports.delete');

  if (!hasAnyAction) return null;

  const toggleDropdown = () => {
    setdropdownOpen(!dropdownOpen);
  };

  const handleDeleteClick = () => {
    setdropdownOpen(false);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    setShowDeleteConfirm(false);
    if (onDelete) {
      onDelete(reportId);
    }
  };

  const handleScheduleClick = () => {
    setShowScheduleModal(true);
  };

  const getDropdownContent = () => {
    return (
      <MenuSection>
        <IfPermission perm="ui-reports.favorite-reports.manage">
          <FavoriteButton reportId={reportId} asDropdownItem />
        </IfPermission>

        <IfPermission perm="ui-reports.scheduled-reports.create">
          <Button
            buttonStyle="dropdownItem"
            onClick={handleScheduleClick}
            onMouseDown={(e) => e.preventDefault()}
          >
            <Icon icon="calendar">
              <FormattedMessage
                id="ui-reports.report.details.actions.scheduleBtn"
                defaultMessage="Schedule Report"
              />
            </Icon>
          </Button>
        </IfPermission>

        <IfPermission perm="ui-reports.reports.edit">
          <Button
            marginBottom0
            id="report-details-actions-menu-edit-metadata-btn"
            buttonStyle="dropdownItem"
            to={{
              pathname: `/reports/reports-list/${reportId}/edit-metadata`,
              state: { returnSearch: location?.search },
            }}
            onMouseDown={(e) => e.preventDefault()}
            disabled={!canEdit}
          >
            <Icon icon="edit">
              <FormattedMessage
                id="ui-reports.report.details.actions.editMetadataBtn"
                defaultMessage="Edit metadata"
              />
            </Icon>
          </Button>
        </IfPermission>

        <IfPermission perm="ui-reports.reports.edit">
          <Button
            marginBottom0
            id="report-details-actions-menu-edit-query-btn"
            buttonStyle="dropdownItem"
            to={{
              pathname: `/reports/reports-list/${reportId}/edit-query`,
              state: { returnSearch: location?.search },
            }}
            onMouseDown={(e) => e.preventDefault()}
            disabled={!canEdit}
          >
            <Icon icon="edit">
              <FormattedMessage
                id="ui-reports.report.details.actions.editReportBtn"
                defaultMessage="Edit report"
              />
            </Icon>
          </Button>
        </IfPermission>

        <IfPermission perm="ui-reports.reports.delete">
          <Button
            marginBottom0
            id="report-details-actions-menu-delete-btn"
            buttonStyle="dropdownItem"
            onClick={handleDeleteClick}
            onMouseDown={(e) => e.preventDefault()}
            disabled={!canEdit}
          >
            <Icon icon="trash">
              <FormattedMessage
                id="ui-reports.report.details.actions.deleteBtn"
                defaultMessage="Delete report"
              />
            </Icon>
          </Button>
        </IfPermission>
      </MenuSection>
    );
  };

  const renderActionsMenuTrigger = ({ getTriggerProps, open }) => {
    return (
      <FormattedMessage id="ui-reports.importreports">
        {(label) => (
          <div style={{ paddingRight: '0.25em', paddingLeft: '0.25em' }}>
            <Button
              data-test-pane-header-actions-button
              buttonStyle="primary"
              marginBottom0
              ariaLabel={label}
              type="button"
              {...getTriggerProps()}
            >
              <Icon icon="ellipsis" size="large" />
            </Button>
          </div>
        )}
      </FormattedMessage>
    );
  };

  const renderActionsMenu = ({ open }) => (
    <DropdownMenu open={open}>{getDropdownContent()}</DropdownMenu>
  );

  return (
    <>
      <Dropdown
        id="report-details-actions-menu-dropdown"
        renderTrigger={renderActionsMenuTrigger}
        renderMenu={renderActionsMenu}
        open={dropdownOpen}
        onToggle={toggleDropdown}
        placement="bottom-end"
        relativePosition
        focusHandlers={{ open: () => null }}
      />
      <ConfirmationModal
        open={showDeleteConfirm}
        heading={
          <FormattedMessage
            id="ui-reports.report.delete.confirm.heading"
            defaultMessage="Delete report?"
          />
        }
        message={
          <FormattedMessage
            id="ui-reports.report.delete.confirm.message"
            defaultMessage="Are you sure you want to delete this report? This action cannot be undone."
          />
        }
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        confirmLabel={
          <FormattedMessage
            id="ui-reports.report.delete.confirm.btn"
            defaultMessage="Delete"
          />
        }
      />
      <ScheduleReportModal
        open={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        reportId={reportId}
        reportName={report?.name}
      />
    </>
  );
}

ReportDetailsActions.propTypes = {
  report:   PropTypes.object,
  onDelete: PropTypes.func,
  canEdit:  PropTypes.bool,
  location: PropTypes.object,
};

export default ReportDetailsActions;
